import {
    Component,
    Element,
    Event,
    EventEmitter,
    // eslint-disable-next-line unused-imports/no-unused-imports
    h,
    Host,
    Listen,
    Prop,
    State,
    Watch,
} from '@stencil/core';
import {
    getApolloThemeFromTheme,
    ThemeType,
} from '@uipath/portal-shell-util';

import { SettingClickedEvent } from '../../models/events/settingClickedEvent';
import { Service } from '../../models/service';
import { Features } from '../../utils/featureFlags/featureFlagConstants';
import { getFeatureFlag } from '../../utils/featureFlags/featureFlags';
import { t } from '../../utils/localization/loc';
import Logger from '../../utils/logger/logger';
import { getMoreTileProps } from '../../utils/menu';
import { MoreTileMenu } from './components/moreTileMenuV3';

let TILE_HEIGHT = 56;
let MENU_ITEM_HEIGHT = 32;
const PROFILE_HEIGHT = 80;
const MENU_PADDING_HEIGHT = 8;

@Component({
    tag: 'ap-nav',
    styleUrl: 'ap-nav.scss',
    shadow: true,
})
export class ApNav {
    @Element() el: HTMLApNavElement;

    // #region Properties

    /**
     * Items to be displayed in the menu
     * @memberof ApNav
     */
    @Prop() services: Service[] = [];

    /**
     * Expanded state of the menu
     *
     * @memberof ApNav
     */
    @Prop() isMenuExpanded: boolean;

    /**
     * Theme
     * @memberof ApNav
     */
    @Prop() theme: ThemeType = 'autoTheme';

    /**
     * Language
     * @memberof ApNav
     */
    @Prop() language: string = 'en';

    /**
     * (Internal Only) Force enable the new left nav design
     * @memberof ApNav
     */
    @Prop() forceEnableLeftNavRedesign: boolean = false;

    // #endregion

    // #region Events

    /**
    * Trigger a custom event for a settings item clicked
    * @event ApNav
    */
    @Event({
        eventName: 'serviceClicked',
        composed: true,
        cancelable: true,
        bubbles: true,
    }) serviceClicked: EventEmitter<SettingClickedEvent>;

    /**
    * Triggered when logout was clicked on the portal menu
    * @event ApNav
    */
    @Event({
        eventName: 'portalMenuClose',
        composed: true,
        cancelable: true,
        bubbles: true,
    }) portalMenuClose: EventEmitter;

    // #endregion

    // #region Fields

    private currentMenuIndex = -1;
    private menuItemsArr: HTMLElement[] = [];
    private moreWrapperRoot: HTMLElement | null;

    // #endregion

    // #region States

    @State() private moreOpen = false;
    @State() private windowHeight: number | null = null;

    // #endregion

    // #region Watchers

    @Watch('isMenuExpanded')
    isMenuExpandedChanged(newValue) {
        if (newValue && this.moreOpen) {
            this.toggleMoreMenu();
        }
    }

    // #endregion

    // #region Life Cycle Methods
    componentWillLoad() {
        this.setWindowHeight();
    }

    componentDidLoad() {
        const moreWrapperRoot = this.el?.shadowRoot?.querySelector<HTMLElement>('#more-wrapper-root');
        if (moreWrapperRoot) {
            this.moreWrapperRoot = moreWrapperRoot;
        }
    }

    // #endregion

    // #region Watchers
    // #endregion

    // #region Listener

    @Listen('resize', { target: 'window' })
    windowResize(_event: UIEvent) {
        this.setWindowHeight();
    }

    /**
     * Handle keyboard navigation
     * Close menu on Keyboard ESC and TAB click
     * @param ev
     */
    @Listen('keydown')
    handleKeyDown(ev: KeyboardEvent) {
        if (this.moreOpen) {
            switch (ev.key) {
                case 'Enter':
                    ev.preventDefault();
                    this.handleEnterKey();
                    break;

                case 'ArrowUp':
                    ev.preventDefault();
                    this.handleMove({
                        down: false,
                        trap: true,
                    });
                    break;

                case 'ArrowDown':
                    ev.preventDefault();
                    this.handleMove({
                        down: true,
                        trap: true,
                    });
                    break;

                case 'Tab':
                    ev.preventDefault();
                    this.handleMove({
                        down: !ev.shiftKey,
                        trap: false,
                    });
                    break;

                case 'Escape':
                    this.moreOpen = false;
                    this.resetMenuFocus();
                    break;
            }
        }
    }

    @Listen('portalMenuClose')
    listenToPortalMenuClose(_event: CustomEvent) {
        this.moreOpen = false;
    }

    // #endregion

    render() {
        const enableLeftNavRedesign = getFeatureFlag(Features.EnableLeftNavRedesign.name) || this.forceEnableLeftNavRedesign;
        const enableLeftNavServiceRevamp = getFeatureFlag(Features.EnableLeftNavServiceRevamp.name);

        TILE_HEIGHT = enableLeftNavRedesign ? 48 : 56;
        MENU_ITEM_HEIGHT = enableLeftNavRedesign ? 48 : 32;

        const buttonItems = this.services.filter(service => !service.isMore);
        const moreMenuItems = this.services.filter(service => service.isMore);
        const shrunkButtonItems = this.getTilesList(buttonItems, moreMenuItems);

        const restButtonItems = buttonItems.filter(service => !shrunkButtonItems.some(nonMenuService =>
            nonMenuService.id === service.id,
        )).concat(...moreMenuItems);

        const moreServiceTileProps = getMoreTileProps(this.language);
        const moreMenuTop = this.getMoreMenuTopPosition(
            // account for the "MORE PRODUCTS" label
            enableLeftNavServiceRevamp ? restButtonItems.length + 1 : restButtonItems.length,
            shrunkButtonItems.length,
        );

        return (
            <Host class={getApolloThemeFromTheme(this.theme)}>
                <nav
                    class={`pt-menu-group${ this.isMenuExpanded ? ' expanded apollo-scrollbar' : '' }`}
                    data-test="ap-nav"
                    role="navigation"
                    aria-label={t('nav-aria-label', this.language)}
                >
                    <div class="pt-top-button" role="list">
                        {this.mapToServiceTiles(shrunkButtonItems, enableLeftNavRedesign)}
                        { this.isMenuExpanded && restButtonItems.length > 0 && ([
                            <portal-divider
                                type="horizontal"
                                ignoreWidthMargins={!enableLeftNavRedesign}
                                bothMarginSize={enableLeftNavRedesign}
                                marginSize="xxxl"
                                horizontalMarginSize="h-xxl"
                            />,
                            <h4 key='h4' class={`more-products${enableLeftNavRedesign ? ' redesign' : ''}`}>{t('more_products', this.language)}</h4>,
                            this.mapToServiceTiles(restButtonItems, enableLeftNavRedesign),
                        ])}
                        {
                            restButtonItems.length > 0 && !this.isMenuExpanded && (
                                <portal-service-tile
                                    {...moreServiceTileProps}
                                    serviceClickHandler={(e) => {
                                        e.preventDefault();

                                        this.toggleMoreMenu();
                                    }}
                                    showName={!enableLeftNavRedesign}
                                    active={this.moreOpen}
                                    serviceid={moreServiceTileProps.id}
                                    handled={false}
                                    handleSpaceAsClick={true}
                                    role="listitem"
                                    forceEnableLeftNavRedesign={this.forceEnableLeftNavRedesign}
                                />
                            )
                        }
                    </div>

                    <div class="pt-filler"/>
                </nav>

                <MoreTileMenu
                    open={this.moreOpen}
                    top={moreMenuTop}
                    services={restButtonItems}
                    serviceClickedEvent={this.serviceClicked}
                    onMoreMenuClose={(e) => this.portalMenuClose.emit(e)}
                />

            </Host>
        );
    }

    // #region Private Methods

    private mapToServiceTiles(services?: Service[], enableLeftNavRedesign?: boolean) {
        return services?.map(button =>
            <portal-service-tile
                {...button}
                showName={!enableLeftNavRedesign || !!this.isMenuExpanded}
                serviceid={button.id}
                listMode={this.isMenuExpanded}
                role="listitem"
                forceEnableLeftNavRedesign={this.forceEnableLeftNavRedesign}
            />,
        ) ?? [];
    }

    private setWindowHeight() {
        this.windowHeight = this.el?.parentElement?.clientHeight ?? null;
    }

    private getTilesList = (buttonItems: Service[], moreServices: Service[] = []) => {
        const totalHeight = this.windowHeight ? (this.windowHeight - PROFILE_HEIGHT) : null;
        if (!totalHeight) {
            Logger.debug([ 'ap-nav', 'menu-items' ], 'window inner height is undefined. Returning all as default.');
            this.moreOpen = false;
            return buttonItems;
        }

        const numTiles = Math.trunc(totalHeight / TILE_HEIGHT);
        if (numTiles >= buttonItems.length && !moreServices.length) {
            Logger.debug([ 'ap-nav', 'menu-items' ], 'height is enough for all tiles to fit');
            this.moreOpen = false;
            return buttonItems;
        }
        Logger.debug([ 'ap-nav', 'menu-items' ], `maximum number of tiles is ${numTiles}`);

        const activeIndex = buttonItems.findIndex(item => !!item.active);
        if (activeIndex < 0) {
            Logger.debug([ 'ap-nav', 'menu-items' ], 'current active item is not found in settings list');
            return buttonItems.slice(0, Math.max(numTiles - 1, 1));
        } else if (activeIndex < Math.max(numTiles - 1, 1)) {
            Logger.debug([ 'ap-nav', 'menu-items' ], `current active item is outside of menu at index ${activeIndex}`);
            return buttonItems.slice(0, Math.max(numTiles - 1, 1));
        } else if (numTiles - 2 < 0) {
            Logger.debug([ 'ap-nav', 'menu-items' ], 'mininum 2 tiles shown reached');
            return [ buttonItems[activeIndex] ];
        }
        Logger.debug([ 'ap-nav', 'menu-items' ], `current active item is inside of menu at index ${activeIndex}`);
        return [ ...buttonItems.slice(0, numTiles - 2), buttonItems[activeIndex] ];
    };

    private getMoreMenuTopPosition(restTiles: number, displayedTiles: number) {
        const moreTileTop = PROFILE_HEIGHT + (displayedTiles * TILE_HEIGHT);
        const menuLength = restTiles * MENU_ITEM_HEIGHT + MENU_PADDING_HEIGHT;

        return !this.windowHeight || this.windowHeight > moreTileTop + menuLength ? moreTileTop : this.windowHeight - menuLength;
    }

    private toggleMoreMenu() {
        Logger.info([ 'ap-nav', 'more' ], 'toggle more menu');
        this.moreOpen = !this.moreOpen;
        this.resetMenuFocus();
        this.prepareMenuItemArray();
    }

    private setMenuFocus(index: number, menuListArray: HTMLElement[]) {
        const menuItemEl = menuListArray[index];
        if (menuItemEl.classList.contains('open')) {
            menuItemEl.classList.remove('open');
        }
        menuItemEl.focus();
    }

    private prepareMenuItemArray() {
        this.menuItemsArr = [];
        this.moreWrapperRoot?.
            querySelectorAll<HTMLElement>(`div.pt-modal > ${this.moreOpen ? 'a >' : ''} div.pt-menu-item-base`)
            .forEach(element => {
                this.menuItemsArr.push(element);
            });
    }

    private resetMenuFocus() {
        const activeItem = this.moreWrapperRoot?.querySelector('.pt-visible');
        if (activeItem) {
            activeItem.classList.remove('pt-visible');
        }
        this.currentMenuIndex = -1;
    }

    /**
     *  Handle `ENTER` key
     */
    private handleEnterKey() {
        const activeItem = this.moreWrapperRoot?.querySelector<HTMLButtonElement>('button:focus');
        if (activeItem) {
            activeItem.click();
        } else if (this.moreOpen) {
            this.toggleMoreMenu();
        }
        this.resetMenuFocus();
    }

    private handleMove = ({
        down = true,
        trap = false,
    }: {
        menuListArray?: HTMLElement[];
        down?: boolean;
        trap?: boolean;
    } = {}) => {
        let newIndex;

        if (down) {
            newIndex = this.currentMenuIndex < this.menuItemsArr.length - 1 ? this.currentMenuIndex + 1 : 0;
        } else {
            newIndex = this.currentMenuIndex > 0 ? this.currentMenuIndex - 1 : this.menuItemsArr.length - 1;
        }

        if (!trap && (
            (newIndex === 0 && (this.currentMenuIndex === this.menuItemsArr.length - 1)) ||
            (newIndex === this.menuItemsArr.length - 1 && [ 0, -1 ].includes(this.currentMenuIndex))
        )) {
            this.moreOpen = false;
            this.resetMenuFocus();

            this.currentMenuIndex = -1;
            return;
        }

        this.currentMenuIndex = newIndex;
        this.setMenuFocus(this.currentMenuIndex, this.menuItemsArr);

    };

    // #endregion
}

